import dayjs from 'dayjs';

import {
  COMMENT_RESOURCE_LABEL_BY_TYPE,
  COMMUNITY_COMMENT_RESOURCE_TYPES,
  COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET,
  COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET,
  COMMUNITY_PLATFORM_FILTER_WIDGET,
  COMMUNITY_SENTIMENT_FILTER_WIDGET,
  failedRuleDeleteTooltipMessage,
  failedRuleHideTooltipMessage,
  failedRuleLikePermissionOrDeletedTooltipMessage,
  failedRuleLikeRateLimitTooltipMessage,
  failedRuleLikeTooltipMessage,
  failedRulePresetReplyTooltipMessage,
  failedRuleSendDmTooltipMessage,
  LIKE_RULE_ERROR_STATUS,
} from '@/app/community/constants';
import {
  MESSAGE_RULE_FILTER_OPTIONS,
  RULE_ACTION_KEYS,
} from '@/app/community/constants/inboxAutomation';
import { isVisionAgentEnabled } from '@/app/community/utils/visionAgentFlag';

export function formatMessageRuleFilterValue(filter) {
  switch (filter.key) {
    case COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.QUESTION.key:
      return COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.QUESTION.label;
    case COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.SPAM.key:
      return COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET.SPAM.label;
    case MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_TYPE.key:
      return (
        COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET[filter.value].formattedLabel ||
        COMMUNITY_MESSAGE_TYPE_FILTER_WIDGET[filter.value].label
      );
    case MESSAGE_RULE_FILTER_OPTIONS.CHANNEL.key:
      return COMMUNITY_PLATFORM_FILTER_WIDGET[filter.value].title;
    case MESSAGE_RULE_FILTER_OPTIONS.TAG.key:
      return Object.values(filter.value)[0];
    case MESSAGE_RULE_FILTER_OPTIONS.SENTIMENT.key:
      return COMMUNITY_SENTIMENT_FILTER_WIDGET[filter.value].title;
    case MESSAGE_RULE_FILTER_OPTIONS.MESSAGE_CONTENT.key:
      return COMMUNITY_MESSAGE_CONTENT_FILTER_WIDGET[filter.value].label;
    case MESSAGE_RULE_FILTER_OPTIONS.REPLIED.key:
      return null;
    default:
      return filter.value;
  }
}

export function ruleExecutedMessageFormatter(
  messageRules,
  checkRuleDeleted,
  undoAction,
  deleteRuleExecuteMessage,
  editRuleAction,
  undoActionFunction,
) {
  return (
    messageRules
      // Hide rule execution records for close (archive) and assign actions since the User
      // Interaction Log popover already covers these.
      ?.filter(
        (rule) =>
          !rule.action?.[RULE_ACTION_KEYS.ARCHIVE] && !rule.action?.[RULE_ACTION_KEYS.ASSIGNEE_ID],
      )
      .map((rule) => {
        const isRuleDeleted = checkRuleDeleted(rule.ruleId);
        const isInteractionDeleted = !!rule.action?.[RULE_ACTION_KEYS.DELETE];
        const isIntegrationAction = !!rule.action?.integration;
        const isPresetReply = !!rule.action?.[RULE_ACTION_KEYS.PRESET_REPLY_ID];
        const isPrivateReply = !!rule.action?.[RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID];
        const cannotUndoAction =
          isInteractionDeleted || isIntegrationAction || isPresetReply || isPrivateReply;
        const visionAgentEnabled = isVisionAgentEnabled();
        const dropdownOptions = [
          {
            label: 'Undo Action',
            disabled: cannotUndoAction,
            command: cannotUndoAction
              ? null
              : async () => {
                  await undoAction(rule, deleteRuleExecuteMessage, undoActionFunction);
                },
            icon: 'refresh-ccw',
          },
          {
            label: visionAgentEnabled ? 'Edit Agent Task' : 'Edit Automation Rule',
            disabled: isRuleDeleted,
            tooltip: isRuleDeleted
              ? visionAgentEnabled
                ? 'This task has been deleted and can no longer be edited'
                : 'This rule has been deleted and can no longer be edited'
              : null,
            command: isRuleDeleted ? null : () => editRuleAction(rule.ruleId, true),
            ruleId: rule.ruleId,
            icon: 'edit',
          },
        ];
        return {
          id: rule.id,
          resourceType: COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED,
          props: {
            comment: { ...rule, text: rule.triggeredRuleName },
            messageDatetime: dayjs.utc(rule.triggeredAt).format('YYYY-MM-DDTHH:mm:ss'),
            label:
              COMMENT_RESOURCE_LABEL_BY_TYPE[
                COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_EXECUTED
              ],
            message: rule.triggeredRuleName,
            dropdownOptions,
            sender: {},
            action: rule.action,
          },
          createdAt: dayjs.utc(rule.triggeredAt),
          variant: 'automation',
        };
      }) ?? []
  );
}

// The rate-limit variants all surface the same "rate limit" tooltip.
const LIKE_RATE_LIMIT_ERROR_STATUSES = [
  LIKE_RULE_ERROR_STATUS.BURST_RATE_LIMIT,
  LIKE_RULE_ERROR_STATUS.PLATFORM_RATE_LIMIT,
  LIKE_RULE_ERROR_STATUS.BUSINESS_USE_CASE_RATE_LIMIT,
];

// Resolves the like-failure tooltip from the backend's `error_status`.
function getLikeFailedTooltip(errorStatus, isVisionAgent) {
  if (LIKE_RATE_LIMIT_ERROR_STATUSES.includes(errorStatus)) {
    return failedRuleLikeRateLimitTooltipMessage(isVisionAgent);
  }
  if (errorStatus === LIKE_RULE_ERROR_STATUS.UNAVAILABLE) {
    return failedRuleLikePermissionOrDeletedTooltipMessage(isVisionAgent);
  }
  return failedRuleLikeTooltipMessage(isVisionAgent);
}

export function ruleFailedMessageFormatter(messageRules) {
  const isVisionAgent = isVisionAgentEnabled();

  return messageRules.map((rule) => {
    const isDeleteAction =
      rule.action === RULE_ACTION_KEYS.DELETE || rule.action?.[RULE_ACTION_KEYS.DELETE];
    const isPrivateReplyAction =
      rule.action === RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID ||
      rule.action?.[RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID];
    const isPresetReplyAction =
      rule.action === RULE_ACTION_KEYS.PRESET_REPLY_ID ||
      rule.action?.[RULE_ACTION_KEYS.PRESET_REPLY_ID];
    const isLikeAction =
      rule.action === RULE_ACTION_KEYS.LIKE || rule.action?.[RULE_ACTION_KEYS.LIKE];

    let resourceType;
    let errorTooltip;
    let failedKey;

    if (isDeleteAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_DELETE_FAILED;
      errorTooltip = failedRuleDeleteTooltipMessage(isVisionAgent);
      failedKey = 'deleteFailed';
    } else if (isPrivateReplyAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_SEND_DM_FAILED;
      errorTooltip = failedRuleSendDmTooltipMessage(isVisionAgent);
      failedKey = 'sendDmFailed';
    } else if (isPresetReplyAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_PRESET_REPLY_FAILED;
      errorTooltip = failedRulePresetReplyTooltipMessage(isVisionAgent);
      failedKey = 'presetReplyFailed';
    } else if (isLikeAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_LIKE_FAILED;
      errorTooltip = getLikeFailedTooltip(rule.errorStatus, isVisionAgent);
      failedKey = 'likeFailed';
    } else {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_HIDE_FAILED;
      errorTooltip = failedRuleHideTooltipMessage(isVisionAgent);
      failedKey = 'hideFailed';
    }

    const result = {
      id: rule.id,
      resourceType,
      props: {
        comment: { ...rule, text: rule.triggeredRuleName },
        messageDatetime: dayjs.utc(rule.triggeredAt).format('YYYY-MM-DDTHH:mm:ss'),
        sender: {},
        isError: true,
        action: rule.action,
      },
      variant: 'error',
      createdAt: dayjs.utc(rule.triggeredAt),
      errorTooltip,
      [failedKey]: true,
    };

    return result;
  });
}

export function processRuleErrorStatus(rule) {
  if (rule.errorStatus) {
    const isDeleteAction = rule.action?.[RULE_ACTION_KEYS.DELETE];
    const isPrivateReplyAction = rule.action?.[RULE_ACTION_KEYS.PRIVATE_REPLY_PRESET_REPLY_ID];
    const isPresetReplyAction = rule.action?.[RULE_ACTION_KEYS.PRESET_REPLY_ID];
    const isLikeAction = rule.action?.[RULE_ACTION_KEYS.LIKE];

    let resourceType;
    if (isDeleteAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_DELETE_FAILED;
    } else if (isPrivateReplyAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_SEND_DM_FAILED;
    } else if (isPresetReplyAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_PRESET_REPLY_FAILED;
    } else if (isLikeAction) {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_LIKE_FAILED;
    } else {
      resourceType = COMMUNITY_COMMENT_RESOURCE_TYPES.COMMUNITY_RULE_HIDE_FAILED;
    }

    return { ...rule, resourceType, isError: true, errorMessage: rule.errorStatus };
  }
  return rule;
}

/**
 * Recursively check if any rule in the list contains the key-value pair in its FILTERS
 */
export function hasMatchInFilters(brandRules, targetKey, targetValue) {
  // Internal recursive helper to traverse nested filter items
  const searchFilters = (filterNode) => {
    if (!filterNode?.items?.length) return false;

    return filterNode.items.some((item) => {
      // Special handling for 'tag' key where value is an object { ID: "text" }
      if (item.key === 'tag' && targetKey === 'tag') {
        if (Object.prototype.hasOwnProperty.call(item.value, targetValue)) return true;
      }
      // Standard key-value matching
      if (item.key === targetKey && item.value === targetValue) return true;

      // Recursive call for nested items
      return item.items ? searchFilters(item) : false;
    });
  };

  // Check if any rule has a matching filter
  return brandRules.some((rule) => searchFilters(rule.detail?.filters));
}

/**
 * Check if any rule in the list contains the key-value pair in its ACTIONS
 */
export function hasMatchInActions(brandRules, targetKey, targetValue) {
  return brandRules.some((rule) => {
    const actions = rule.detail?.actions;
    if (!actions || typeof actions !== 'object') return false;

    // Special handling for 'tags' array structure
    if (targetKey === 'tags' && Array.isArray(actions.tags)) {
      return actions.tags.some((tagObj) =>
        Object.prototype.hasOwnProperty.call(tagObj, targetValue),
      );
    }

    // Standard key-value matching
    return actions[targetKey] === targetValue;
  });
}
